import React, { useState, useEffect, useCallback } from "react";
import { AxiosError } from "axios";

import {
  IDog,
  IRibbonRoomResponse,
  ISportProgress,
  INoseworkProgress,
  IScentworkProgress,
  IUKCRallyProgress,
  IBarnHuntProgress,
} from "@eagerdog/interfaces";
import { Constants, getTitleTrackerId, isTitleTrackerVisible } from "@eagerdog/constants";

import { apiService } from "../../../services/api.service";
import { helperService } from "../../../services/helper.service";
import { toast } from "../../../components/Toast/ToastManager";
import { Modal, useModal } from "../../../components/Modal/Modal";

import FastCATCard from "../FastCATCard/FastCATCard";
import NoseworkCard from "../NoseworkCard/NoseworkCard";
import ScentworkCard from "../ScentworkCard/ScentworkCard";
import FetchCard from "../FetchCard/FetchCard";
import ObedienceCard from "../ObedienceCard/ObedienceCard";
import RallyCard from "../RallyCard/RallyCard";
import UKCObedienceCard from "../UKCObedienceCard/UKCObedienceCard";
import UKCRallyCard from "../UKCRallyCard/UKCRallyCard";
import BarnHuntCard from "../BarnHuntCard/BarnHuntCard";
import TrackerSettingsModal from "../TrackerSettingsModal/TrackerSettingsModal";

import Button from "../../../components/Button/Button";
import "./RibbonRoomTab.scss";

interface IProps {
  dog: IDog;
  isOwner: boolean;
}

const RibbonRoomTab: React.FC<IProps> = ({ dog, isOwner }) => {
  const [ribbonData, setRibbonData] = useState<IRibbonRoomResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedClub, setSelectedClub] = useState<string>('All');
  const [syncing, setSyncing] = useState<boolean>(false);
  const [currentDog, setCurrentDog] = useState<IDog>(dog);
  const confirmSyncModal = useModal();
  const trackerSettingsModal = useModal();

  // Filter sports by selected sanctioning club AND visibility settings
  const filteredSports = ribbonData?.sports?.filter(sport => {
    // Filter by club
    if (selectedClub !== 'All' && sport.sanctioning_club !== selectedClub) {
      return false;
    }
    
    // Filter by visibility settings (shared helper — same rule as mobile)
    const trackerId = getTitleTrackerId(sport.sanctioning_club, sport.show_type);
    return isTitleTrackerVisible(trackerId, currentDog.tracker_visibility_settings);
  }) || [];

  const handleSyncClick = () => {
    confirmSyncModal.toggle();
  };

  const handleSettingsClick = () => {
    trackerSettingsModal.toggle();
  };

  const handleTrackerSettingsSave = (updatedDog: IDog) => {
    setCurrentDog(updatedDog);
  };

  const handleConfirmSync = async () => {
    if (!dog._id || !isOwner) return;
    
    confirmSyncModal.toggle(); // Close modal
    setSyncing(true);
    
    try {
      const response = await apiService.migrateDogScores(dog._id);
      
      const totalMigrated = response.results?.reduce((acc: number, r: any) => acc + r.successful, 0) || 0;
      const totalSkipped = response.results?.reduce((acc: number, r: any) => acc + r.skipped, 0) || 0;
      
      toast.show({
        title: "Historical Scores Synced Successfully",
        content: totalMigrated > 0 
          ? `${totalMigrated} score${totalMigrated !== 1 ? 's' : ''} imported to Title Tracker${totalSkipped > 0 ? ` (${totalSkipped} already imported)` : ''}`
          : "All scores were already imported to Title Tracker",
        duration: 5000,
        type: "success"
      });
      
      // Reload ribbon room data to show new titles
      loadRibbonData();
    } catch (error: any) {
      console.error('Migration error:', error);
      toast.show({
        title: "Sync Failed",
        content: error.response?.data?.message || "Failed to sync historical scores. Please try again.",
        duration: 10000,
        errorDetails: error,
        type: "fail"
      });
    } finally {
      setSyncing(false);
    }
  };

  const loadRibbonData = useCallback(() => {
    if (dog._id) {
      setLoading(true);
      const isLoggedIn = helperService.isLoggedIn();
      
      // Use public endpoint if not logged in or not owner
      const fetchData = (isLoggedIn && isOwner) 
        ? apiService.getRibbonRoomData(dog._id)
        : apiService.getPublicRibbonRoomData(dog._id);
      
      fetchData.then((response) => {
        if (!response) {
          console.error('Response is undefined/null');
          setRibbonData({ sports: [] });
          setLoading(false);
          return;
        }
        
        if (!response.sports) {
          console.error('Response.sports is undefined:', response);
          setRibbonData({ sports: [] });
          setLoading(false);
          return;
        }
        
        setRibbonData(response);
        setLoading(false);
      }).catch((e: AxiosError) => {
        console.error('Ribbon Room API Error:', e);
        toast.show({
          title: "Ribbon Room",
          content: "Unable to load title tracking data",
          duration: 10000,
          errorDetails: e,
          type: "fail"
        });
        setLoading(false);
      });
    }
  }, [dog._id, isOwner]);

  useEffect(() => {
    loadRibbonData();
  }, [loadRibbonData]);

  if (loading) {
    return (
      <div className="ribbonRoomTab loading">
        <div className="loadingMessage">Loading Ribbon Room...</div>
      </div>
    );
  }

  if (!ribbonData || filteredSports.length === 0) {
    // Check if it's empty because all trackers are hidden
    const allTrackersHidden = ribbonData?.sports && ribbonData.sports.length > 0 && filteredSports.length === 0;
    
    return (
      <div className="ribbonRoomTab empty">
        <div className="emptyState">
          <div className="emptyIcon">🏆</div>
          <h2>Welcome to {dog.call_name}'s Ribbon Room!</h2>
          <p>
            {allTrackersHidden
              ? "All trackers are hidden. Click the settings icon to show trackers."
              : selectedClub === 'All'
              ? "The Ribbon Room tracks your dog's titles and achievements across different sports."
              : `No ${selectedClub} titles tracked yet.`
            }
          </p>
          {!allTrackersHidden && (
            <div className="emptyHint">
              <strong>Supported Sports:</strong>
              <ul>
                <li>AKC Fast CAT - Track points and earn BCAT, DCAT, FCAT titles!</li>
                <li>UKC Nosework - Track qualifications and earn element, level, and champion titles!</li>
                <li>AKC Scent Work - Track element qualifications and earn titles!</li>
                <li>AKC Fetch - Track levels and earn titles with unique judge requirements!</li>
                <li>AKC Obedience - Track CD, CDX, UD, UDX titles!</li>
                <li>AKC Rally - Track RN, RI, RA, RE, RM, RAE, RACH titles with points system!</li>
              </ul>
            </div>
          )}
          {isOwner && allTrackersHidden && (
            <div className="emptyActions">
              <Button variant="default" onClick={handleSettingsClick}>
                Open Settings
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="ribbonRoomTab">
      {/* Sanctioning Club Filter */}
      <div className="clubFilters">
        <div className="filterButtons">
          <Button
            variant={selectedClub === 'All' ? 'default' : 'secondary'}
            onClick={() => setSelectedClub('All')}
          >
            All
          </Button>
          <Button
            variant={selectedClub === 'AKC' ? 'default' : 'secondary'}
            onClick={() => setSelectedClub('AKC')}
          >
            AKC
          </Button>
          <Button
            variant={selectedClub === 'UKC' ? 'default' : 'secondary'}
            onClick={() => setSelectedClub('UKC')}
          >
            UKC
          </Button>
          <Button
            variant={selectedClub === 'BHA' ? 'default' : 'secondary'}
            onClick={() => setSelectedClub('BHA')}
          >
            BHA
          </Button>
        </div>
        
        {isOwner && (
          <div className="actionButtons">
            <button 
              className="settingsButton"
              onClick={handleSettingsClick}
              title="Tracker Display Settings"
            />
            
            <button 
              className={`syncButton ${syncing ? 'syncing' : ''}`}
              onClick={handleSyncClick}
              disabled={syncing}
              title="Sync historical scores to Title Tracker"
            >
              <svg 
                className="syncIcon" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2"
              >
                <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/>
              </svg>
            </button>
          </div>
        )}
      </div>

      <p className="subtitle">Track titles, achievements, and progress across all sports</p>
      
      {/* Syncing Overlay */}
      {syncing && (
        <div className="syncingOverlay">
          <div className="syncingContent">
            <div className="syncingSpinner">
              <svg 
                className="spinner" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2"
              >
                <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/>
              </svg>
            </div>
            <h3>Syncing Historical Scores...</h3>
            <p>This may take a few minutes depending on the number of results</p>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      <Modal
        isShown={confirmSyncModal.isShown}
        hide={confirmSyncModal.toggle}
        className="syncConfirmationModal"
        modalContent={
          <div className="syncConfirmModal">
            <div className="modalIcon">
              <svg 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2"
              >
                <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/>
              </svg>
            </div>
            
            <h3>Import Past Results</h3>
            
            <p className="modalDescription">
              This will import and calculate all title progress from {dog.call_name}'s past results on the EagerDog platform.
            </p>
            
            <p className="modalNote">
              <strong>Note:</strong> This process may take a few minutes depending on the number of results. Your progress will be automatically updated when complete.
            </p>
            
            <div className="modalActions">
              <Button 
                variant="secondary" 
                onClick={confirmSyncModal.toggle}
              >
                Cancel
              </Button>
              <Button 
                variant="default" 
                onClick={handleConfirmSync}
              >
                Start Sync
              </Button>
            </div>
          </div>
        }
      />
      <div className="sportsGrid">
        {filteredSports.map((sport) => {
          // Render the appropriate sport card based on show_type
          // Only pass onRunUpdated callback if user is the owner
          const onRunUpdated = isOwner ? loadRibbonData : undefined;
          
          if (sport.show_type === Constants.show_type.fast_cat) {
            return <FastCATCard key={`${sport.sanctioning_club}_${sport.show_type}`} dog={dog} sportData={sport as ISportProgress} onRunUpdated={onRunUpdated} />;
          }
          
          if (sport.show_type === Constants.show_type.nosework) {
            return <NoseworkCard key={`${sport.sanctioning_club}_${sport.show_type}`} dog={dog} noseworkData={sport as INoseworkProgress} onRunUpdated={onRunUpdated} />;
          }
          
          if (sport.show_type === Constants.show_type.scentwork) {
            return <ScentworkCard key={`${sport.sanctioning_club}_${sport.show_type}`} dog={dog} scentworkData={sport as IScentworkProgress} onRunUpdated={onRunUpdated} />;
          }
          
          if (sport.show_type === Constants.show_type.fetch) {
            return <FetchCard key={`${sport.sanctioning_club}_${sport.show_type}`} dog={dog} fetchData={sport as any} onRunUpdated={onRunUpdated} />;
          }

          if (sport.show_type === Constants.show_type.obedience) {
            return <ObedienceCard key={`${sport.sanctioning_club}_${sport.show_type}`} dog={dog} obedienceData={sport as any} onRunUpdated={onRunUpdated} />;
          }

          if (sport.show_type === Constants.show_type.rally_obedience) {
            return <RallyCard key={`${sport.sanctioning_club}_${sport.show_type}`} dog={dog} rallyData={sport as any} onRunUpdated={onRunUpdated} />;
          }
          if (sport.show_type === Constants.show_type.ukc_obedience) {
            return <UKCObedienceCard key={`${sport.sanctioning_club}_${sport.show_type}`} dog={dog} ukcObedienceData={sport as any} onRunUpdated={onRunUpdated} />;
          }
          if (sport.show_type === Constants.show_type.ukc_rally) {
            return <UKCRallyCard key={`${sport.sanctioning_club}_${sport.show_type}`} dog={dog} ukcRallyData={sport as IUKCRallyProgress} onRunUpdated={onRunUpdated} />;
          }

          if (sport.show_type === Constants.show_type.barn_hunt) {
            return <BarnHuntCard key={`${sport.sanctioning_club}_${sport.show_type}`} dog={dog} barnHuntData={sport as IBarnHuntProgress} onRunUpdated={onRunUpdated} />;
          }
          
          // Future: Add other sport cards here
          return null;
        })}
      </div>

      {/* Tracker Settings Modal */}
      {isOwner && (
        <Modal
          isShown={trackerSettingsModal.isShown}
          hide={trackerSettingsModal.toggle}
          className="trackerSettingsModalContainer"
          modalContent={
            <TrackerSettingsModal
              dog={currentDog}
              onClose={trackerSettingsModal.toggle}
              onSave={handleTrackerSettingsSave}
            />
          }
        />
      )}
    </div>
  );
};

export default RibbonRoomTab;
